import { defineStore } from 'pinia';
import 'pinia-plugin-persistedstate';
import _ from 'lodash';
import type { MppPolicy } from '@Types/MppPolicy';
import { getRequestSummaryByActivitySessionApi } from '@Api/activitySession.api';
import type { RequestSummary } from '@Types/RequestSummary.interface';
import type { Address, WiringInstructions } from '@Types/WiringInstrucions.interface';
import { useWiringInstructionsForm } from '@Use/wiringInstructions/useWiringInstructionsForm';
import {
    clearFlowAnchorRequestIdFromSessionStorage,
    isValidRequestSummary,
    readFlowAnchorRequestIdFromSessionStorage,
    writeFlowAnchorRequestIdToSessionStorage,
} from './requestSessionStore.utils';
import { useRequestDetailsStore } from './requestDetails.store';

/** True when another tab overwrote the activity session (anchored request id no longer matches API). */
export type SyncSessionResult = {
    sessionContextOverwritten: boolean;
    requestSummaryChanged: boolean;
    requestSummary: RequestSummary | null;
};

export interface ConfirmRejectionState {
    reason: string;
    additionalDetails: string | null;
}

function getInitialCollectWiringInstructions(): WiringInstructions {
    const { createEmptyWiringInstructions } = useWiringInstructionsForm();
    return createEmptyWiringInstructions();
}

/**
 * DS-2779 A.1: how long a successful syncSession result stays trusted before
 * the next route navigation has to round-trip the BFF again.
 *
 * Before this cache the router's `beforeEach` guard was firing
 * `getRequestSummaryByActivitySessionApi` on every nav, and the source view's
 * button spinner stayed live until that returned — a slow BFF call surfaced
 * as ~7-second "Continue" hangs in the send flow and made mid-load refreshes
 * leave the activity session in a half-fetched state.
 *
 * Cross-tab session-overwrite detection (the original reason for the per-nav
 * sync) is delayed by at most this TTL. Bootstrap routes and the wire-
 * instructions-ready override path still force a fresh sync.
 */
export const SYNC_SESSION_TTL_MS = 30_000;

/**
 * Exactly what the session store writes to localStorage. Exported so tests can assert against
 * the real list rather than a copy: anything added here outlives the request in the
 * participant's browser, so PII belongs on a plain state field instead (see
 * collectAccountHolderDateOfBirth).
 */
export const PERSISTED_SESSION_KEYS = [
    'selectedMppPolicy',
    'confirmWiringInstructions',
    'confirmPersonalAddress',
    'collectWiringInstructions',
    'accountHolders',
    'requestSummary',
    'mppPurchaseDecision',
    'confirmRejection',
    'inviteToken',
    'qrSessionToken',
];

export const useRequestSessionStore = defineStore('requestSession', {
    state: (): {
        requestSummary: RequestSummary | null;
        selectedMppPolicy: MppPolicy | null;
        confirmWiringInstructions: WiringInstructions | null;
        confirmPersonalAddress: Address | null;
        collectWiringInstructions: WiringInstructions;
        // Separate holder names for the collect form. The joined string still lives on
        // collectWiringInstructions.creditTo.accountName; this list is only used to refill
        // the inputs when the user comes back from review.
        accountHolders: string[];
        mppPurchaseDecision: boolean | null;
        confirmRejection: ConfirmRejectionState | null;
        flowAnchoredRequestId: string | null;
        inviteToken: string | null;
        qrSessionToken: string | null;
        // DS-2779: NOT persisted — freshness must be re-established per page load.
        lastSyncAt: number | null;
        /**
         * PLAT-3373: the account holder's date of birth, in flight between the Collect
         * account-details step and submit. Deliberately NOT on collectWiringInstructions and
         * deliberately absent from persist.pick below: that store is written to localStorage and
         * nothing clears it after finalize, so the date would sit in the participant's browser
         * indefinitely alongside their name and address. It is verification-only PII — it goes to
         * AVS and is not stored anywhere else, server or client.
         */
        collectAccountHolderDateOfBirth: string | null;
    } => ({
        requestSummary: null,
        collectAccountHolderDateOfBirth: null,
        selectedMppPolicy: null,
        confirmWiringInstructions: null,
        confirmPersonalAddress: null,
        collectWiringInstructions: getInitialCollectWiringInstructions(),
        accountHolders: [],
        mppPurchaseDecision: null,
        confirmRejection: null,
        flowAnchoredRequestId: null,
        inviteToken: null,
        qrSessionToken: null,
        lastSyncAt: null,
    }),

    getters: {
        requestId: (state) => state.requestSummary?.id ?? null,
        isRequestComplete: (state) => state.requestSummary?.isComplete ?? false,
        isRequestExpired: (state) => state.requestSummary?.isExpired ?? false,
    },

    actions: {
        setQrSessionToken(token: string | null): void {
            this.qrSessionToken = token;
        },

        _clearCommonSessionState(): void {
            this.selectedMppPolicy = null;
            this.confirmWiringInstructions = null;
            this.confirmPersonalAddress = null;
            this.collectWiringInstructions = getInitialCollectWiringInstructions();
            this.accountHolders = [];
            this.mppPurchaseDecision = null;
            this.confirmRejection = null;
        },

        /** Clears form state, request summary, and flow anchor. */
        _handleSessionContextOverwritten(): void {
            this._clearCommonSessionState();
            this.flowAnchoredRequestId = null;
            this.requestSummary = null;
            this.inviteToken = null;
            this.qrSessionToken = null;
            this.lastSyncAt = null;
            clearFlowAnchorRequestIdFromSessionStorage();
            useRequestDetailsStore().$reset();
        },

        /** If persisted summary was for another request id, clears form state so stale selections are not reused. */
        _handleRequestSummaryChanged(fetchedRequestId: string): void {
            const hadOtherRequest =
                this.requestSummary != null &&
                this.requestSummary.id !== fetchedRequestId;
            if (hadOtherRequest) {
                this._clearCommonSessionState();
                this.inviteToken = null;
                this.qrSessionToken = null;
                useRequestDetailsStore().$reset();
            }
        },

        /**
         * Resets all session state and clears the sessionStorage request id. Called when the
         * user navigates to a new request via invite link.
         *
         * PS-1169: deliberately does NOT clear the minted QR token marker. setupApp's
         * handleInviteEntry calls this on the Intellicheck capture return and then re-applies
         * qrSessionToken from the URL. Clearing the marker here would leave the originating
         * device holding a token it appears not to have minted, i.e. misread as the scanning
         * phone, which is the bug PS-1169 fixed.
         */
        resetForNewRequest(): void {
            this.$reset();
            clearFlowAnchorRequestIdFromSessionStorage();
        },

        /**
         * Compares the fetched request id against the id this tab recorded on first load.
         * Handles two mismatch cases:
         *   - another tab silently changed the session (overwrite) → signals caller to redirect
         *   - this tab is starting fresh on a new request → updates the stored id and clears stale form state
         */
        _handleSessionRequestMismatch(fetchedId: string): { sessionContextOverwritten: boolean } {
            const thisTabRequestId = this.flowAnchoredRequestId ?? readFlowAnchorRequestIdFromSessionStorage();
            const requestChanged = thisTabRequestId != null && fetchedId !== thisTabRequestId;
            const tabHadEstablishedRequest = this.requestSummary != null && this.requestSummary.id === thisTabRequestId;

            if (requestChanged && tabHadEstablishedRequest) {      // another tab silently changed the session
                this._handleSessionContextOverwritten();
                return { sessionContextOverwritten: true };
            }

            if (requestChanged && !tabHadEstablishedRequest) {     // this tab is starting fresh on a new request
                this.flowAnchoredRequestId = fetchedId;
                writeFlowAnchorRequestIdToSessionStorage(fetchedId);
                this._handleRequestSummaryChanged(fetchedId);
            }

            return { sessionContextOverwritten: false };
        },

        /**
         * Fetches request summary from the BFF, detects cross-tab session overwrite vs a fresh request start,
         * updates `requestSummary`, and establishes `flowAnchoredRequestId` on first successful load.
         *
         * DS-2779 A.1 — within {@link SYNC_SESSION_TTL_MS} of the last successful sync the cached
         * `requestSummary` is reused without a BFF roundtrip, unless `force = true`. The router's
         * `beforeEach` guard sets `force` on bootstrap navigation and on the wire-instructions-ready
         * override path so those still re-anchor against the server.
         */
        async syncSession(options: { force?: boolean } = {}): Promise<SyncSessionResult> {
            const { force = false } = options;
            if (
                !force
                && this.requestSummary != null
                && this.lastSyncAt != null
                && Date.now() - this.lastSyncAt < SYNC_SESSION_TTL_MS
            ) {
                return {
                    sessionContextOverwritten: false,
                    requestSummaryChanged: false,
                    requestSummary: this.requestSummary,
                };
            }

            const requestSummary = await getRequestSummaryByActivitySessionApi();

            if (!isValidRequestSummary(requestSummary)) {
                return {
                    sessionContextOverwritten: false,
                    requestSummaryChanged: false,
                    requestSummary: null
                };
            }

            const { sessionContextOverwritten } = this._handleSessionRequestMismatch(requestSummary.id);
            if (sessionContextOverwritten) {
                return {
                    sessionContextOverwritten: true,
                    requestSummaryChanged: false,
                    requestSummary: null
                };
            }

            const requestSummaryChanged = !_.isEqual(requestSummary, this.requestSummary);
            if (requestSummaryChanged) {
                this._handleRequestSummaryChanged(requestSummary.id);
            }

            this.requestSummary = requestSummary;
            if (!this.flowAnchoredRequestId) {
                this.flowAnchoredRequestId = requestSummary.id;
                writeFlowAnchorRequestIdToSessionStorage(requestSummary.id);
            }

            this.lastSyncAt = Date.now();

            return {
                sessionContextOverwritten: false,
                requestSummaryChanged, requestSummary
            };
        },
    },

    persist: {
        pick: PERSISTED_SESSION_KEYS,
    },
});
